import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Sparkles, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { BlogCover } from "../components/BlogCover";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";

const SITE_ORIGIN = (typeof window !== "undefined" && window.location.origin) || "https://vivealogrande.com";

const BlogPost = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const { language } = useApp();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const isEs = language === "es";

    useEffect(() => {
        // Reset state so switching from one post to another shows the loading state (no stale hero).
        setPost(null);
        setRelated([]);
        setLoading(true);
        setNotFound(false);
        window.scrollTo({ top: 0, behavior: "instant" });

        (async () => {
            try {
                const res = await api.get(`/blog/posts/${slug}`);
                setPost(res.data);
            } catch (e) {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    // Fetch related posts once the current post loads — pick 3 by tag overlap,
    // fallback to most recent.
    useEffect(() => {
        if (!post) return;
        (async () => {
            try {
                const res = await api.get("/blog/posts?limit=30");
                const all = (res.data.posts || []).filter((p) => p.slug !== post.slug);
                const currTags = new Set((post.tags || []).map((t) => t.toLowerCase()));
                const scored = all
                    .map((p) => {
                        const overlap = (p.tags || []).filter((t) => currTags.has(t.toLowerCase())).length;
                        return { ...p, _score: overlap };
                    })
                    .sort((a, b) => b._score - a._score || (b.published_at || "").localeCompare(a.published_at || ""));
                setRelated(scored.slice(0, 3));
            } catch (e) {
                setRelated([]);
            }
        })();
    }, [post]);

    // Inject SEO tags + JSON-LD once the post loads.
    useEffect(() => {
        if (!post) return;
        const title = isEs ? post.title_es : post.title_en;
        const desc = isEs ? post.excerpt_es : post.excerpt_en;
        const canonical = `${SITE_ORIGIN}/blog/${post.slug}`;
        document.title = `${title} | Caesar Blog`;

        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("description", desc || "");
        setMeta("og:title", title, "property");
        setMeta("og:description", desc || "", "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", canonical, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", title);
        setMeta("twitter:description", desc || "");

        // Absolute cover image URL for OG/Twitter cards.
        if (post.cover_url) {
            const absoluteCover = post.cover_url.startsWith("http")
                ? post.cover_url
                : `${SITE_ORIGIN}${post.cover_url}`;
            setMeta("og:image", absoluteCover, "property");
            setMeta("twitter:image", absoluteCover);
        }

        // Canonical
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.setAttribute("rel", "canonical");
            document.head.appendChild(link);
        }
        link.setAttribute("href", canonical);

        // Article JSON-LD
        const ldId = "blog-post-jsonld";
        document.getElementById(ldId)?.remove();
        const ld = document.createElement("script");
        ld.id = ldId;
        ld.type = "application/ld+json";
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: title,
            description: desc,
            image: post.cover_url ? [post.cover_url.startsWith("http") ? post.cover_url : `${SITE_ORIGIN}${post.cover_url}`] : undefined,
            datePublished: post.published_at,
            dateModified: post.published_at,
            author: { "@type": "Person", name: post.author || "Caesar" },
            publisher: {
                "@type": "Organization",
                name: "Vive a lo Grande",
                logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/favicon.png` },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            inLanguage: isEs ? "es" : "en",
        });
        document.head.appendChild(ld);

        return () => {
            document.getElementById(ldId)?.remove();
        };
    }, [post, isEs]);

    const fmtDate = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleDateString(isEs ? "es-ES" : "en-US", { year: "numeric", month: "long", day: "numeric" });
        } catch {
            return "";
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] grid place-items-center">
                <div className="text-sm text-[hsl(var(--muted-foreground))]">{isEs ? "Cargando..." : "Loading..."}</div>
            </div>
        );
    }
    if (notFound || !post) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] grid place-items-center px-6">
                <div className="text-center max-w-md">
                    <h1 className="font-display font-bold text-2xl text-[hsl(var(--foreground))]">
                        {isEs ? "Post no encontrado" : "Post not found"}
                    </h1>
                    <p className="mt-2 text-[hsl(var(--muted-foreground))]">
                        {isEs ? "Este artículo no existe o fue movido." : "This article doesn't exist or was moved."}
                    </p>
                    <Button
                        data-testid="blog-post-notfound-back"
                        onClick={() => navigate("/blog")}
                        className="mt-6 pill bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
                    >
                        {isEs ? "Ver todos los posts" : "See all posts"}
                    </Button>
                </div>
            </div>
        );
    }

    const title = isEs ? post.title_es : post.title_en;
    const content = isEs ? post.content_es : post.content_en;
    const readTime = isEs ? post.read_time_es : post.read_time_en;

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="blog-post-page">
            <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link
                        to="/blog"
                        data-testid="blog-post-back"
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {isEs ? "Blog" : "Blog"}
                    </Link>
                    <button
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2 text-[hsl(var(--primary))]"
                    >
                        <BrandLogo size={26} />
                        <span className="font-display font-bold text-base tracking-tight text-[hsl(var(--foreground))]">
                            Vive a lo Grande
                        </span>
                    </button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-3 mb-4 text-xs text-[hsl(var(--muted-foreground))]">
                        <span className="font-bold tracking-[0.24em] uppercase text-[hsl(var(--accent))]">
                            {isEs ? "Blog de Caesar" : "Caesar's Blog"}
                        </span>
                        <span>·</span>
                        <span>{fmtDate(post.published_at)}</span>
                        {readTime ? (
                            <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {readTime} min</span>
                            </>
                        ) : null}
                    </div>
                    <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] leading-[1.08] tracking-[-0.02em] text-3xl md:text-5xl">
                        {title}
                    </h1>
                    <div className="mt-6 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                        <span className="w-8 h-8 rounded-full grid place-items-center bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">
                            <Sparkles className="w-4 h-4" />
                        </span>
                        <span>{isEs ? "Por Caesar" : "By Caesar"}</span>
                    </div>
                </motion.div>

                {post.cover_url ? (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="mt-10 rounded-2xl overflow-hidden border border-[hsl(var(--border))]"
                    >
                        <BlogCover src={post.cover_url} title={title} />
                    </motion.div>
                ) : null}

                <article
                    data-testid="blog-post-content"
                    className="prose prose-invert mt-10 md:mt-14 max-w-none
                               prose-headings:font-display prose-headings:tracking-tight prose-headings:text-[hsl(var(--foreground))]
                               prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4
                               prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                               prose-p:text-[hsl(var(--foreground))]/85 prose-p:leading-relaxed
                               prose-strong:text-[hsl(var(--foreground))]
                               prose-a:text-[hsl(var(--primary))] prose-a:no-underline hover:prose-a:underline
                               prose-li:text-[hsl(var(--foreground))]/85
                               prose-blockquote:border-l-[hsl(var(--primary))] prose-blockquote:text-[hsl(var(--foreground))]/85
                               prose-code:text-[hsl(var(--primary))] prose-code:bg-[hsl(var(--card))] prose-code:px-1 prose-code:rounded"
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
                </article>

                {post.tags?.length ? (
                    <div className="mt-10 pt-6 border-t border-[hsl(var(--border))] flex flex-wrap gap-2">
                        {post.tags.map((tg) => (
                            <span
                                key={tg}
                                className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
                            >
                                {tg}
                            </span>
                        ))}
                    </div>
                ) : null}

                <div className="mt-12 pt-8 border-t border-[hsl(var(--border))] flex items-center justify-between gap-4 flex-wrap">
                    <Link
                        to="/blog"
                        data-testid="blog-post-see-more"
                        className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                    >
                        ← {isEs ? "Ver todos los posts" : "See all posts"}
                    </Link>
                    <Button
                        data-testid="blog-post-cta-home"
                        onClick={() => navigate("/")}
                        className="pill bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
                    >
                        {isEs ? "Probar Vive a lo Grande" : "Try Vive a lo Grande"}
                    </Button>
                </div>

                {/* Related posts rail — 3 more Caesar posts ranked by tag overlap */}
                {related.length > 0 ? (
                    <section className="mt-16 pt-10 border-t border-[hsl(var(--border))]" data-testid="blog-related">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="text-[10px] md:text-xs font-black tracking-[0.28em] text-[hsl(var(--accent))] uppercase">
                                {isEs ? "Sigue leyendo" : "Keep reading"}
                            </span>
                            <span className="h-px flex-1 bg-[hsl(var(--border))]" />
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {related.map((p) => {
                                const rt = isEs ? p.title_es : p.title_en;
                                const rex = isEs ? p.excerpt_es : p.excerpt_en;
                                const rrt = isEs ? p.read_time_es : p.read_time_en;
                                return (
                                    <Link
                                        key={p.post_id}
                                        to={`/blog/${p.slug}`}
                                        data-testid={`blog-related-${p.slug}`}
                                        className="group block rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-all"
                                    >
                                        <BlogCover src={p.cover_url} title={rt} />
                                        <div className="p-4">
                                            {rrt ? (
                                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1 mb-1.5">
                                                    <Clock className="w-3 h-3" /> {rrt} min
                                                </div>
                                            ) : null}
                                            <h3 className="font-display font-bold text-[hsl(var(--foreground))] text-base leading-snug tracking-tight line-clamp-3 group-hover:text-[hsl(var(--primary))] transition-colors">
                                                {rt}
                                            </h3>
                                            {rex ? (
                                                <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-2">
                                                    {rex}
                                                </p>
                                            ) : null}
                                            <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] group-hover:gap-2 transition-all">
                                                {isEs ? "Leer" : "Read"} <ArrowRight className="w-3 h-3" />
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                ) : null}
            </main>
        </div>
    );
};

export default BlogPost;
